import { useCallback, useMemo } from 'react';
import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  Alert,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  EvacZone,
  EvacZoneStatus,
  FormEvacZoneStatus,
  RegionEvacZoneStyle,
} from 'shared/types';
import { getResponsiveFontSize } from 'theme/watchdutyTheme.utils';
import { RichTextEditor } from 'components/RichTextEditor';
import { GeoEventRegion } from 'features/geoEvents/geoEventRegion.types';
import { EvacZoneStatuses } from '../../../../constants';
import { PendingEvacZonesWarning } from '../PendingEvacZonesWarning';
import { EvacuationZonesTable } from '../EvacuationZonesTable/EvacuationZonesTable';
import { CustomEvacuationLevelNotes } from './CustomEvacuationLevelNotes';
import { EvacZoneStatusChange } from '../../types';
import { useEvacZoneSync } from './useEvacZoneSync';
import { updateEvacZoneStatuses } from './utils';
import { getEffectiveEvacZones } from '../../utils';
import { UnstructuredEvacuations } from '../UnstructuredEvacuations';

type EvacuationsProps = {
  evacZones: EvacZone[];
  zoneStyles: RegionEvacZoneStyle[];
  shouldRenderTable: boolean;
  showUnstructuredEvacuations?: boolean;
  /** The incident's counties. Defaults to the form's `regions`. */
  incidentRegions?: GeoEventRegion[];
  disabled?: boolean;
};

const createAlphabeticalComparator = <T, U extends string>(
  getKey: (obj: T) => U,
) => {
  return (a: T, b: T): number => {
    const valA = getKey(a).toLowerCase();
    const valB = getKey(b).toLowerCase();
    return valA.localeCompare(valB);
  };
};

const processEvacZonesByStatus = (
  evacZoneStatuses: FormEvacZoneStatus[],
  pendingReviewZoneIds: Set<string>,
  status: EvacZoneStatus,
): FormEvacZoneStatus[] => {
  return evacZoneStatuses
    .filter((zone) => zone.status === status)
    .map((zone) => ({
      ...zone,
      evacZone: {
        ...zone.evacZone,
        isPendingReview: pendingReviewZoneIds.has(zone.evacZone.uidV2),
      },
    }));
};

const useStyles = makeStyles()((theme) => ({
  alertInfo: {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    '& svg': {
      fontSize: getResponsiveFontSize('1.25rem'),
    },
  },
  editor: {
    minHeight: 42,
  },
  notesEditor: {
    minHeight: 84,
  },
}));

export const Evacuations = ({
  evacZones,
  zoneStyles,
  shouldRenderTable,
  showUnstructuredEvacuations = false,
  incidentRegions,
  disabled = false,
}: EvacuationsProps): JSX.Element => {
  const { control, formState, watch, setValue, clearErrors } = useFormContext();
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [
    evacZoneStatuses,
    temporarilyDisplayEvacZones,
    customOrders,
    customWarnings,
    customAdvisories,
    customShelterInPlace,
    releasePendingZoneUids,
    regions,
    unstructuredEvacuationRegionIds,
  ] = watch([
    'evacZoneStatuses',
    'temporarilyDisplayEvacZones',
    'customOrders',
    'customWarnings',
    'customAdvisories',
    'customShelterInPlace',
    'releasePendingZoneUids',
    'regions',
    'unstructuredEvacuationRegionIds',
  ]) as [
    FormEvacZoneStatus[],
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
    string[] | undefined,
    GeoEventRegion[] | undefined,
    number[] | undefined,
  ];

  // Zones accepted on the form (pre-submission) preview their pending name.
  const { effectiveEvacZones: displayEvacZones, effectiveZoneNamesByUid } =
    useMemo(
      () => getEffectiveEvacZones(evacZones, releasePendingZoneUids),
      [evacZones, releasePendingZoneUids],
    );

  const pendingReviewZoneIds = useMemo(
    () =>
      new Set(
        evacZones
          .filter((evacZone) => evacZone.isPendingReview)
          .map((evacZone) => evacZone.uidV2),
      ),
    [evacZones],
  );

  const {
    evacZonesOrders,
    evacZonesWarnings,
    evacZonesAdvisories,
    evacZonesShelterInPlace,
  } = useMemo(() => {
    // Form state holds the zone names captured when the form initialized.
    // Prefer the current zone names (including accepted-rename previews).
    const sortedEvacZoneStatuses = evacZoneStatuses
      .map((zone) => {
        const liveDisplayName = effectiveZoneNamesByUid.get(
          zone.evacZone.uidV2,
        );
        if (!liveDisplayName || liveDisplayName === zone.evacZone.displayName) {
          return zone;
        }
        return {
          ...zone,
          evacZone: { ...zone.evacZone, displayName: liveDisplayName },
        };
      })
      .sort(
        createAlphabeticalComparator<FormEvacZoneStatus, string>(
          (item) => item.evacZone.displayName,
        ),
      );

    const ordersEvacZones = processEvacZonesByStatus(
      sortedEvacZoneStatuses,
      pendingReviewZoneIds,
      EvacZoneStatuses.orders,
    );
    const warningsEvacZones = processEvacZonesByStatus(
      sortedEvacZoneStatuses,
      pendingReviewZoneIds,
      EvacZoneStatuses.warnings,
    );
    const advisoriesEvacZones = processEvacZonesByStatus(
      sortedEvacZoneStatuses,
      pendingReviewZoneIds,
      EvacZoneStatuses.advisories,
    );
    const shelterInPlaceEvacZones = processEvacZonesByStatus(
      sortedEvacZoneStatuses,
      pendingReviewZoneIds,
      EvacZoneStatuses.shelterInPlace,
    );

    return {
      evacZonesOrders: ordersEvacZones,
      evacZonesWarnings: warningsEvacZones,
      evacZonesAdvisories: advisoriesEvacZones,
      evacZonesShelterInPlace: shelterInPlaceEvacZones,
    };
  }, [evacZoneStatuses, pendingReviewZoneIds, effectiveZoneNamesByUid]);

  const { customEvacValues, onCheckboxChange } = useEvacZoneSync({
    customOrders,
    customWarnings,
    customAdvisories,
    customShelterInPlace,
  });

  const hasEvacZones = evacZones.length > 0;

  const handleEvacZoneStatusChange = useCallback(
    (changes: EvacZoneStatusChange[]) => {
      const { updatedStatuses, changeCount } = updateEvacZoneStatuses(
        changes,
        evacZoneStatuses,
        displayEvacZones,
      );

      setValue('evacZoneStatuses', updatedStatuses, { shouldDirty: true });
      return changeCount;
    },
    [evacZoneStatuses, displayEvacZones, setValue],
  );

  return (
    <Grid container spacing={2} sx={{ width: '100%' }}>
      {!hasEvacZones && (
        <Grid size={12}>
          <Alert severity="info" className={classes.alertInfo}>
            {t('geoEventEvacuations.noEvacZones')}
          </Alert>
        </Grid>
      )}

      {hasEvacZones && (
        <Grid size={12}>
          <FormControlLabel
            disabled={
              evacZonesOrders.length > 0 ||
              evacZonesWarnings.length > 0 ||
              evacZonesAdvisories.length > 0 ||
              evacZonesShelterInPlace.length > 0 ||
              disabled
            }
            control={
              <Checkbox
                name="temporarilyDisplayEvacZones"
                checked={temporarilyDisplayEvacZones}
                onChange={(event) => {
                  setValue(
                    'temporarilyDisplayEvacZones',
                    event.target.checked,
                    {
                      shouldDirty: true,
                    },
                  );
                  clearErrors('temporarilyDisplayEvacZones');
                }}
              />
            }
            label={t('geoEventEvacuations.inputs.temporarilyDisplayEvacZones')}
          />
        </Grid>
      )}

      <PendingEvacZonesWarning evacZones={evacZones} />

      {shouldRenderTable && hasEvacZones && (
        <>
          <Grid size={12}>
            <EvacuationZonesTable
              disabled={temporarilyDisplayEvacZones || disabled}
              evacZones={displayEvacZones}
              zoneStyles={zoneStyles}
              formZones={evacZoneStatuses}
              handleStatusChange={handleEvacZoneStatusChange}
            />
          </Grid>

          {showUnstructuredEvacuations && (
            <Grid size={12}>
              <UnstructuredEvacuations
                regions={incidentRegions ?? regions ?? []}
                selectedRegionIds={unstructuredEvacuationRegionIds ?? []}
                onChange={(regionIds) =>
                  setValue('unstructuredEvacuationRegionIds', regionIds, {
                    shouldDirty: true,
                  })
                }
                disabled={disabled}
              />
            </Grid>
          )}
        </>
      )}

      <Grid size={12}>
        <Typography
          component="h3"
          sx={{ fontWeight: 'bold', marginTop: 2, marginBottom: 1 }}
        >
          {t('geoEventEvacuations.customNotes')}
        </Typography>
      </Grid>

      <CustomEvacuationLevelNotes
        customFieldName="evacuationOrders"
        customEvacZones={customOrders}
        customEvacZoneString={customEvacValues.evacuationOrders}
        zoneStyles={zoneStyles}
        level={EvacZoneStatuses.orders}
        hasEvacZones={hasEvacZones}
        hideWhenDisabled
        disabled={disabled}
        toggleCustomZone={(enabled) =>
          onCheckboxChange({
            enabled,
            checkboxName: 'customOrders',
            fieldName: 'evacuationOrders',
            fieldEvacZones: evacZonesOrders,
          })
        }
      />

      <CustomEvacuationLevelNotes
        customFieldName="evacuationWarnings"
        customEvacZones={customWarnings}
        customEvacZoneString={customEvacValues.evacuationWarnings}
        zoneStyles={zoneStyles}
        level={EvacZoneStatuses.warnings}
        hasEvacZones={hasEvacZones}
        hideWhenDisabled
        disabled={disabled}
        toggleCustomZone={(enabled) =>
          onCheckboxChange({
            enabled,
            checkboxName: 'customWarnings',
            fieldName: 'evacuationWarnings',
            fieldEvacZones: evacZonesWarnings,
          })
        }
      />

      <CustomEvacuationLevelNotes
        customFieldName="evacuationAdvisories"
        customEvacZones={customAdvisories}
        customEvacZoneString={customEvacValues.evacuationAdvisories}
        zoneStyles={zoneStyles}
        level={EvacZoneStatuses.advisories}
        hasEvacZones={hasEvacZones}
        hideWhenDisabled
        disabled={disabled}
        toggleCustomZone={(enabled) =>
          onCheckboxChange({
            enabled,
            checkboxName: 'customAdvisories',
            fieldName: 'evacuationAdvisories',
            fieldEvacZones: evacZonesAdvisories,
          })
        }
      />

      <CustomEvacuationLevelNotes
        customFieldName="evacuationShelterInPlace"
        customEvacZones={customShelterInPlace}
        customEvacZoneString={customEvacValues.evacuationShelterInPlace}
        zoneStyles={zoneStyles}
        level={EvacZoneStatuses.shelterInPlace}
        hasEvacZones={hasEvacZones}
        hideWhenDisabled
        disabled={disabled}
        toggleCustomZone={(enabled) =>
          onCheckboxChange({
            enabled,
            checkboxName: 'customShelterInPlace',
            fieldName: 'evacuationShelterInPlace',
            fieldEvacZones: evacZonesShelterInPlace,
          })
        }
      />

      <Grid size={12}>
        <Controller
          name="evacuationNotes"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            return (
              <>
                <RichTextEditor
                  id="field-control-evacuation-notes"
                  data-testid="evacuation-notes"
                  label={t('evacZones.notes')}
                  initialValue={formState.defaultValues?.evacuationNotes || ''}
                  onChange={field.onChange}
                  editable={!formState.isSubmitting && !disabled}
                  error={Boolean(fieldState.error)}
                  editorClassName={classes.notesEditor}
                  onBlur={field.onBlur}
                />
                {Boolean(fieldState.error) && (
                  <FormHelperText
                    error
                    sx={{ marginLeft: 1.75, marginRight: 1.75 }}
                  >
                    {fieldState.error?.message}
                  </FormHelperText>
                )}
              </>
            );
          }}
        />
      </Grid>
    </Grid>
  );
};
